import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

import { faqItems } from '@/data/faq-items';
import { serviceItems } from '@/data/service-items';
import { translate } from '@/translations';

export const SEO = () => {
  useLocation();

  const currentUrl = window.location.origin + window.location.pathname + window.location.hash;

  const businessSchema = {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    '@id': 'https://intechnity.com/#localbusiness',
    name: 'Intechnity',
    url: 'https://intechnity.com/',
    description: translate('seo:description'),
    telephone: '+48 500 570 440',
    email: 'contact@intechnity.com',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'ul. Technologiczna 2',
      addressLocality: 'Opole',
      postalCode: '45-839',
      addressCountry: 'PL',
    },
    areaServed: 'Global',
    sameAs: [
      'https://www.facebook.com/intechnitycom',
      'https://github.com/Intechnity-com',
      'https://pl.linkedin.com/company/intechnity-com',
      'https://www.linkedin.com/showcase/intechnitycom/',
      'https://www.instagram.com/intechnity_com/',
      'https://themanifest.com/company/intechnity',
    ],
    logo: 'https://intechnity.com/favicon.ico',
    image: 'https://intechnity.com/favicon.ico',
    foundingDate: '2024',
    knowsAbout: [
      'custom software development',
      'web app development',
      'mobile app development',
      'desktop app development',
      'flutter',
      'react native',
      'erp',
      'angular',
      'react',
      '.net',
      'azure',
      'aws',
      'saas software development',
      'odoo integrations',
    ],
  };

  const contactSectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    mainEntity: {
      '@type': 'Organization',
      name: 'Intechnity',
      url: 'https://intechnity.com',
      contactPoint: [
        {
          '@type': 'ContactPoint',
          telephone: '+48 500 570 440',
          contactType: 'Customer Service',
          areaServed: ['PL', 'DE', 'FR', 'ES', 'IT', 'GB', 'US', 'CA', 'NO'],
          availableLanguage: ['Polish', 'German', 'English'],
        },
        {
          '@type': 'ContactPoint',
          email: 'contact@intechnity.com',
          contactType: 'Customer Service',
          areaServed: ['PL', 'DE', 'FR', 'ES', 'IT', 'GB', 'US', 'CA', 'NO'],
          availableLanguage: ['Polish', 'German', 'English'],
        },
      ],
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'ul. Technologiczna 2',
        addressLocality: 'Opole',
        postalCode: '45-839',
        addressCountry: 'PL',
      },
    },
  };

  const contactSchema = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    mainEntity: {
      '@type': 'ContactForm',
      name: translate('contact:title'),
      description: translate('contact:subtitle'),
      action: {
        '@type': 'CommunicateAction',
        target: 'https://www.intechnity.com/contact',
        expectsAcceptanceOf: {
          '@type': 'Offer',
          name: 'Contact Submission',
        },
      },
    },
  };

  const servicesSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Software Development Services',
    provider: {
      '@type': 'Organization',
      name: 'Intechnity',
      url: 'https://intechnity.com',
    },
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Software Development Services',
      itemListElement: [
        ...serviceItems.map((serviceItem) => {
          return {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: translate(serviceItem.titleKey),
              description: translate(serviceItem.descriptionKey),
            },
          };
        }),
      ],
    },
  };

  const faqSchemaData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((section) => ({
      '@type': 'Question',
      name: translate(section.title),
      acceptedAnswer: {
        '@type': 'Answer',
        text: translate(section.content),
      },
    })),
  };

  return (
    <Helmet>
      <title>{translate('seo:title')}</title>
      <meta name="description" content={translate('seo:description')} />
      <link rel="alternate" href={`${currentUrl}?lng=en`} hrefLang="en" />
      <link rel="alternate" href={`${currentUrl}?lng=de`} hrefLang="de" />
      <link rel="alternate" href={`${currentUrl}?lng=pl`} hrefLang="pl" />

      <meta property="og:title" content={translate('seo:title')} />
      <meta property="og:description" content={translate('seo:description')} />
      <meta property="og:image" content="https://intechnity.com/socialCoverImage.jpg" />
      <meta property="og:url" content="https://intechnity.com" />
      <meta property="og:type" content="website" />

      <script type="application/ld+json">{JSON.stringify(businessSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(servicesSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(contactSectionSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(contactSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchemaData)}</script>
    </Helmet>
  );
};
